import { memo, useEffect, useState } from "react";
import type { PrecipNowcast, WeatherSnapshot } from "@chula/shared";
import { Term } from "./Term";

/**
 * HostStrip — the Bangkok "right now" block that used to head the WorldStrip.
 *
 * The six user-editable world clocks that followed it are gone: an operator
 * watching campus does not need Sydney's time, and the row cost a full strip
 * of vertical space on every lens. What stays is the part that is decision-
 * relevant on campus — temperature, feels-like, rain now + 2 h nowcast, wind,
 * AQI — and the live pulse counts that the banner chips summarise.
 *
 * Weather now comes from the same /api/weather feed the rest of the shell
 * uses rather than a separate six-city world-weather fetch.
 */
interface Props {
  bangkokAqi: number | null;
  bangkokPm25: number | null;
  weather: WeatherSnapshot | null;
  precipNowcast: PrecipNowcast | null;
  pulse: {
    iticEvents: number;
    openReports: number;
    news24h: number;
    shuttleLive: number;
  };
  /**
   * fallbackTier of the shuttle feed. Anything but "live" is the clock-driven
   * simulator, and the count is shown with a SIM tag so a scenario fleet is
   * never read as a live headcount.
   */
  shuttleTier?: string;
  /**
   * Single-line glance (temperature · AQI · rain next 2 h) so the shell can
   * give the vertical space to the attention queue. Full strip by default.
   */
  compact?: boolean;
}

function bangkokClock(now: Date): { hms: string; offset: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Bangkok", hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  const offset = new Intl.DateTimeFormat("en", { timeZone: "Asia/Bangkok", timeZoneName: "shortOffset" })
    .formatToParts(now).find((p) => p.type === "timeZoneName")?.value ?? "";
  return { hms: `${get("hour")}:${get("minute")}:${get("second")}`, offset };
}

const fmtTemp = (t: number | null) => (t == null ? "—°" : `${Math.round(t)}°`);
const fmtPct = (p: number | null) => (p == null ? "—" : `${Math.round(p)}%`);
const fmtFix = (n: number | null, d = 1) => (n == null ? "—" : n.toFixed(d));

function pulseColor(n: number, warn: number, bad: number): string {
  if (n >= bad) return "var(--bad)";
  if (n >= warn) return "var(--warn)";
  return "var(--text)";
}

function aqiBand(aqi: number | null): { label: string; color: string } {
  if (aqi == null) return { label: "—", color: "var(--text-3)" };
  if (aqi <= 50) return { label: "good", color: "var(--good)" };
  if (aqi <= 100) return { label: "moderate", color: "var(--warn)" };
  if (aqi <= 150) return { label: "unhealthy SG", color: "var(--bad)" };
  if (aqi <= 200) return { label: "unhealthy", color: "var(--bad)" };
  return { label: "hazardous", color: "var(--crit)" };
}

function rainBadge(p: PrecipNowcast | null): { label: string; sub: string; color: string } {
  if (!p) return { label: "—", sub: "rain forecast unavailable", color: "var(--text-3)" };
  if ((p.resolutionMinutes ?? 15) >= 60) return { label: p.intensity === "dry" ? "DRY FORECAST" : "RAIN FORECAST", sub: `${p.total2hMm.toFixed(1)} mm / 2h model · hourly resolution`, color: p.intensity === "dry" ? "var(--ink-mid)" : "var(--warn)" };
  if (p.intensity === "dry") return { label: "DRY 2H", sub: `${p.total2hMm.toFixed(1)} mm forecast`, color: "var(--good)" };
  const mins = p.minutesToSignificant;
  const sub = `peak ${p.peakMm} mm · ${p.total2hMm.toFixed(1)} mm / 2h`;
  if (p.intensity === "heavy") return { label: mins != null ? `RAIN ${mins}m` : "RAIN NOW", sub, color: "var(--bad)" };
  if (p.intensity === "moderate") return { label: mins != null ? `RAIN ${mins}m` : "RAIN NOW", sub, color: "var(--warn)" };
  return { label: mins != null ? `DRIZZLE ${mins}m` : "DRIZZLE", sub, color: "var(--data)" };
}

export const HostStrip = memo(function HostStrip(props: Props) {
  return props.compact ? <HostStripCompact {...props} /> : <HostStripFull {...props} />;
});

/** The one-line glance. No clock tick: nothing here changes by the second. */
function HostStripCompact({ bangkokAqi, bangkokPm25, weather, precipNowcast }: Props) {
  const aqi = aqiBand(bangkokAqi);
  const rain = rainBadge(precipNowcast);
  return (
    <div className="host-compact mono" role="group" aria-label="Bangkok conditions now">
      <span className="host-compact-item">
        <span className="host-compact-lbl">Bangkok</span>
        <span className="host-compact-val">{fmtTemp(weather?.tempC ?? null)}</span>
        {weather?.feelsLikeC != null && <span className="host-compact-sub">feels {fmtTemp(weather.feelsLikeC)}</span>}
      </span>
      <span className="host-compact-item">
        <span className="host-compact-lbl"><Term k="AQI" /></span>
        <span className="host-compact-val" style={{ color: aqi.color }}>{bangkokAqi ?? "—"}</span>
        <span style={{ color: aqi.color }}>{aqi.label}</span>
        {bangkokPm25 != null && <span className="host-compact-sub"><Term k="PM2.5" /> {bangkokPm25.toFixed(1)}</span>}
      </span>
      <span className="host-compact-item">
        <span className="host-compact-lbl">Rain 2 h</span>
        <span className="host-compact-val" style={{ color: rain.color }}>{rain.label}</span>
        <span className="host-compact-sub">{rain.sub}</span>
      </span>
    </div>
  );
}

function HostStripFull({ bangkokAqi, bangkokPm25, weather, precipNowcast, pulse, shuttleTier }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const aqi = aqiBand(bangkokAqi);
  const rain = rainBadge(precipNowcast);
  const clock = bangkokClock(now);
  const busIsSim = shuttleTier != null && shuttleTier !== "live";

  return (
    <div className="world-strip">
      <section className="world-host">
        <div className="world-host-head">
          <span className="eyebrow mono">Bangkok · host</span>
          <span className="mono caption">{clock.hms} · {clock.offset}</span>
        </div>
        <div className="world-host-row">
          <div className="world-host-now">
            <span className="world-host-temp" style={weather?.tempC == null ? { color: "var(--text-3)" } : undefined}>
              {fmtTemp(weather?.tempC ?? null)}
            </span>
            <span className="world-host-cond">{weather?.condition ?? "—"}</span>
            <span
              className="world-host-feels mono"
              style={weather?.feelsLikeC == null ? { color: "var(--text-3)" } : undefined}
            >
              FEELS {fmtTemp(weather?.feelsLikeC ?? null)}
            </span>
          </div>
          <div className="world-host-stats">
            <div className="world-stat">
              <span className="lbl">HUMIDITY</span>
              <span className="val mono">{fmtPct(weather?.humidity ?? null)}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">WIND</span>
              <span className="val mono">{weather?.windKmh != null ? String(Math.round(weather.windKmh)) : "—"}</span>
              <span className="sub mono">KM/H</span>
            </div>
            <div className="world-stat">
              <span className="lbl"><Term k="AQI" /></span>
              <span className="val mono" style={{ color: aqi.color }}>{bangkokAqi ?? "—"}</span>
              {/* Band word always shown — colour is never the only signal. */}
              <span className="sub mono" style={{ color: aqi.color }}>
                {aqi.label}
                {bangkokPm25 != null && <> · <Term k="PM2.5" /> {bangkokPm25.toFixed(1)}</>}
              </span>
            </div>
            <div className="world-stat">
              <span className="lbl">RAIN 2H</span>
              <span className="val mono" style={{ color: rain.color }}>{rain.label}</span>
              <span className="sub mono" style={{ color: rain.color }}>{rain.sub}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">RAIN NOW</span>
              <span className="val mono">{fmtFix(weather?.precipMm ?? null, 1)}</span>
              <span className="sub mono">MM/H</span>
            </div>
          </div>
          <div className="world-host-pulse" role="group" aria-label="Campus pulse — counts from each feed">
            <div className="pulse-head">
              <span className="dot live" />
              <span className="eyebrow mono">Campus pulse</span>
            </div>
            <div className="pulse-grid">
              <div className="pulse-cell" title="Traffic incidents reported across Bangkok right now (iTIC feed)">
                <span className="lbl">TRAFFIC</span>
                <span className="val mono" style={{ color: pulseColor(pulse.iticEvents, 1, 6) }}>{pulse.iticEvents}</span>
              </div>
              <div className="pulse-cell" title="Citizen problem reports still open (Traffy Fondue / City Reporter)">
                <span className="lbl">REPORTS OPEN</span>
                <span className="val mono" style={{ color: pulseColor(pulse.openReports, 1, 11) }}>{pulse.openReports}</span>
              </div>
              <div className="pulse-cell" title="News stories mentioning Chula in the last 24 hours">
                <span className="lbl">NEWS 24 H</span>
                <span className="val mono">{pulse.news24h}</span>
              </div>
              <div
                className="pulse-cell"
                title={busIsSim
                  ? "CU POP Bus fleet on the map — SIMULATED from the published timetable until the ViaBus GPS feed is wired"
                  : "CU shuttle buses live on the map right now"}
              >
                <span className="lbl">CU BUS{busIsSim && <> · <Term k="SIM" /></>}</span>
                <span className="val mono" style={busIsSim ? { color: "var(--text-2)" } : undefined}>{pulse.shuttleLive}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
